import React, { useState, useEffect, useCallback } from 'react';
import { Nav, Spinner, Alert, Button, Badge } from 'react-bootstrap';
import supabase from '../supabase';

const CATEGORY_ORDER = ['produce','bakery','deli','meat','dairy','frozen','pantry','household','other'];

function groupByCategory(items) {
  const groups = {};
  for (const item of items) {
    const cat = item.category || 'other';
    if (!groups[cat]) groups[cat] = [];
    groups[cat].push(item);
  }
  const ordered = {};
  for (const cat of CATEGORY_ORDER) {
    if (groups[cat]) ordered[cat] = groups[cat];
  }
  for (const [cat, items] of Object.entries(groups)) {
    if (!ordered[cat]) ordered[cat] = items;
  }
  return ordered;
}

export default function Shopping() {
  const [stores, setStores] = useState([]);
  const [activeStore, setActiveStore] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    async function fetchStores() {
      const { data, error } = await supabase
        .from('shopping_items')
        .select('store')
        .eq('archived', false);
      if (error) { setError(error.message); setLoading(false); return; }
      const unique = [...new Set(data.map(r => r.store))].sort();
      setStores(unique);
      if (unique.length > 0) setActiveStore(unique[0]);
      setLoading(false);
    }
    fetchStores();
  }, []);

  const fetchItems = useCallback(async () => {
    if (!activeStore) return;
    const { data, error } = await supabase
      .from('shopping_items')
      .select('*')
      .eq('store', activeStore)
      .eq('archived', false)
      .order('added_at');
    if (error) { setError(error.message); return; }
    setItems(data || []);
  }, [activeStore]);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  useEffect(() => {
    const channel = supabase
      .channel('shopping-changes')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'shopping_items' }, fetchItems)
      .subscribe();
    return () => supabase.removeChannel(channel);
  }, [fetchItems]);

  async function toggleCheck(item) {
    const checked = !item.checked;
    const { error } = await supabase
      .from('shopping_items')
      .update({ checked, checked_at: checked ? new Date().toISOString() : null })
      .eq('id', item.id);
    if (error) { setError(error.message); return; }
    setItems(prev => prev.map(i => i.id === item.id ? { ...i, checked, checked_at: checked ? new Date().toISOString() : null } : i));
  }

  async function clearChecked() {
    const ts = new Date().toISOString();
    const checkedIds = items.filter(i => i.checked).map(i => i.id);
    if (!checkedIds.length) return;
    const { error } = await supabase
      .from('shopping_items')
      .update({ archived: true, archived_at: ts })
      .in('id', checkedIds);
    if (error) { setError(error.message); return; }
    setItems(prev => prev.filter(i => !i.checked));
  }

  const checkedCount = items.filter(i => i.checked).length;
  const grouped = groupByCategory(items);

  if (loading) return <div className="text-center mt-5"><Spinner animation="border" /></div>;

  return (
    <div>
      <div className="page-header">
        <div className="d-flex align-items-center justify-content-between">
          <div>
            <h1>Shopping</h1>
            <p>{items.length} items{checkedCount > 0 ? ` · ${checkedCount} checked` : ''}</p>
          </div>
          {checkedCount > 0 && (
            <Button
              size="sm"
              onClick={clearChecked}
              style={{ background: 'var(--success)', border: 'none', color: '#fff', fontWeight: 600 }}
            >
              Clear checked ({checkedCount})
            </Button>
          )}
        </div>
      </div>

      {error && <Alert variant="danger" dismissible onClose={() => setError(null)}>{error}</Alert>}

      {stores.length === 0 ? (
        <div className="d-card">
          <p style={{ color: 'var(--text-muted)', margin: 0 }}>No active shopping lists.</p>
        </div>
      ) : (
        <div className="d-card" style={{ maxWidth: 580 }}>
          <Nav
            className="shopping-tabs mb-3"
            activeKey={activeStore}
            onSelect={setActiveStore}
            style={{ borderBottom: '1px solid var(--border)' }}
          >
            {stores.map(store => (
              <Nav.Item key={store}>
                <Nav.Link eventKey={store} style={{ textTransform: 'capitalize' }}>
                  {store}
                  {activeStore === store && items.length > 0 && (
                    <Badge
                      className="ms-1"
                      style={{ background: 'var(--accent)', color: '#fff', fontSize: '0.7rem' }}
                    >
                      {items.length}
                    </Badge>
                  )}
                </Nav.Link>
              </Nav.Item>
            ))}
          </Nav>

          {items.length === 0 ? (
            <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem' }}>This list is empty.</p>
          ) : (
            Object.entries(grouped).map(([category, catItems]) => (
              <div key={category} className="mb-3">
                <div className="category-label">{category}</div>
                {catItems.map(item => (
                  <div
                    key={item.id}
                    className="task-item"
                    style={{ cursor: 'pointer' }}
                    onClick={() => toggleCheck(item)}
                  >
                    <div className={`task-check ${item.checked ? 'completed' : ''}`}
                      style={item.checked ? { borderColor: 'var(--success)', background: 'var(--success)', color: '#fff' } : {}}>
                      {item.checked && <i className="bi bi-check" style={{ fontSize: '0.75rem' }} />}
                    </div>
                    <span style={item.checked ? { textDecoration: 'line-through', opacity: 0.45 } : {}}>
                      {item.quantity && (
                        <span style={{ color: 'var(--text-muted)', fontSize: '0.85em', marginRight: 6 }}>
                          {item.quantity}
                        </span>
                      )}
                      {item.item}
                    </span>
                  </div>
                ))}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}
